import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Close, Done, Star, StarBorder } from '@mui/icons-material';
import { IconButton, ListItemIcon, Menu, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectRmRealtimeOn } from 'state/routeMonitoringSlice';
import {
  SavedRouteAndFeature,
  TempRoute,
  selectCurrentSavedRoutes,
  selectInSavedRouteEditMode,
  setCurrentSavedRoute,
  setInSavedRouteEditMode,
  useSavedRouteCreateMutation,
  useSavedRouteUpdateMutation,
} from 'state/savedRouteSlices';
import {
  ProjectFeatures,
  getProjectInfoForSlug,
  selectUser,
} from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectProject,
  selectRmStartDateTime,
  setRouteSelection,
  setWorkflow,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import { SOMETHING_WENT_WRONG_SAVING_MSG, getSavedSuccessMsg } from 'appUtils';
import { EditPopup } from 'features/common/EditPopup';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlTooltip from 'features/common/StlTooltip';
import { dateToString, parseDate } from 'features/common/datetimeUtils';
import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { canManageSavedRoute } from 'features/common/savedRouteUtils';
import { showToast } from 'features/common/toasts/ToastProvider';
import { updateNameError } from 'features/common/utils';
import ZoomToRoute from 'features/map/overlays/ZoomToRoute';
import { useNotificationProject } from 'features/notifications/useNotificationProject';

import { ConfirmDeletePopup } from '../common/ConfirmDeletePopup';
import { useMenu } from './hooks/useMenu';

import commonStyles from '../common/common.module.css';
import styles from './RouteMenu.module.css';

interface IRouteMenu {
  hasAlertEvent: boolean;
  route: SavedRouteAndFeature;
  deleteRoute: (id: string) => void;
}

const DELETE_ROUTE_CUSTOM_MESSAGE =
  "Deleting this route will also delete any alerts exclusively assigned to it. Alerts shared with other routes won't be affected";

export default function RouteMenu({
  route,
  deleteRoute,
  hasAlertEvent,
}: IRouteMenu) {
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState<SavedRouteAndFeature | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState<SavedRouteAndFeature | null>(null);
  const [editRouteName, setEditRouteName] = useState<string>(
    route.savedRoute.routeName,
  );
  const isRealtime = useAppSelector(selectRmRealtimeOn);
  const project_slug = useAppSelector(selectProject);
  const { navigateTo } = useNotificationProject();
  const rmStartDate = useAppSelector(selectRmStartDateTime);
  const editMode = useAppSelector(selectInSavedRouteEditMode);
  const savedRoutes = useAppSelector(selectCurrentSavedRoutes);
  const user = useAppSelector(selectUser);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const projectHasAlerts = userProject?.features.includes(
    ProjectFeatures.ALERTS,
  );

  const projectInfo = getProjectInfoForSlug(user, project_slug);
  const canEditOrDelete = canManageSavedRoute(
    user?.email,
    route.savedRoute.creatorEmail,
    !!projectInfo?.is_sub_admin,
  );
  const manageRouteDisabledReason =
    'Only the route creator or a project sub-admin can edit or delete this route.';

  const [updateSavedRoute] = useSavedRouteUpdateMutation();
  const { anchorEl, open, onClose, onOpen } = useMenu();

  const existingNames = useMemo(
    () =>
      savedRoutes
        ?.filter((el) => !editMode || el.id !== route.id)
        .map((r) => r.savedRoute.routeName.trim().toLowerCase()) || [],
    [savedRoutes, editMode],
  );

  function exitEditMode() {
    dispatch(setInSavedRouteEditMode(false));
  }

  const onRouteStarClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    const val = !route.savedRoute.starred;
    dispatch(
      setCurrentSavedRoute({
        ...route,
        savedRoute: { ...route.savedRoute, starred: val },
      }),
    );
    updateSavedRoute({
      project_slug: project_slug as string,
      savedRouteId: route.id,
      savedRoute: {
        routeName: route.savedRoute.routeName,
        routeSegmentIds: route.savedRoute.routeSegmentIds,
        realizedSegments: route.savedRoute.realizedSegments,
        starred: val,
      },
    });
  };

  const handleEditName = () => {
    if (
      editRouteName !== route.savedRoute.routeName &&
      editRouteName.length > 0
    ) {
      dispatch(
        setCurrentSavedRoute({
          ...route,
          savedRoute: { ...route.savedRoute, routeName: editRouteName },
        }),
      );
      updateSavedRoute({
        project_slug: project_slug as string,
        savedRouteId: route.id,
        savedRoute: {
          ...route.savedRoute,
          routeName: editRouteName,
        },
      });
    }
    exitEditMode();
  };

  const handlePrepareDelete = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsDeleting(route);
      onClose();
    },
    [route, onClose],
  );

  const handlePrepareEdit = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsEditing(route);
      dispatch(setInSavedRouteEditMode(true));
      setEditRouteName(route.savedRoute.routeName);
      onClose();
    },
    [route, onClose],
  );

  const handleManageRouteAlert = useCallback(() => {
    navigateTo.assignRoute(route.id);
  }, [navigateTo, route.id]);

  const handleConfirmDelete = useCallback(() => {
    if (isDeleting) {
      deleteRoute(isDeleting.id);
      setIsDeleting(null);
    }
  }, [deleteRoute, isDeleting]);

  const handleAlertEventClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    navigateTo.log({
      state: {
        routeId: route.savedRoute.id,
        searchTerm: route.savedRoute.routeName,
        date: isRealtime ? undefined : dateToString(parseDate(rmStartDate)),
        resolved: isRealtime ? false : undefined,
      },
    });
  };

  return (
    <>
      {hasAlertEvent && (
        <IconButton
          size="small"
          edge="end"
          title="Notification"
          onClick={handleAlertEventClick}
        >
          <FontAwesomeIcon name="bell" />
        </IconButton>
      )}
      <IconButton
        size="small"
        className={styles.route_star}
        edge="end"
        title="Star"
        onClick={onRouteStarClick}
      >
        {route.savedRoute.starred ? <Star /> : <StarBorder />}
      </IconButton>
      <IconButton
        size="small"
        aria-controls={open ? 'route-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        title="More"
        onClick={onOpen}
      >
        <FontAwesomeIcon name="ellipsis-vertical" />
      </IconButton>
      <Menu
        className={commonStyles.menu}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <OpenTravelTimeMenuItem onClose={onClose} id={route.id} />
        {projectHasAlerts && (
          <MenuItem
            onClick={handleManageRouteAlert}
            className={commonStyles.menu_item}
          >
            <ListItemIcon aria-label="manage" title="Manage">
              <FontAwesomeIcon
                name="arrow-up-right-from-square"
                size="0.75rem"
              />
            </ListItemIcon>
            <Typography variant="caption">Manage Route Alerts</Typography>
          </MenuItem>
        )}
        <StlTooltip
          title={!canEditOrDelete ? manageRouteDisabledReason : ''}
          placement="left"
        >
          <span style={{ display: 'block' }}>
            <MenuItem
              onClick={handlePrepareEdit}
              disabled={!canEditOrDelete}
              className={commonStyles.menu_item}
            >
              <ListItemIcon aria-label="edit" title="Edit">
                <FontAwesomeIcon name="pen" size="0.75rem" />
              </ListItemIcon>
              <Typography variant="caption">Edit Route Name</Typography>
            </MenuItem>
          </span>
        </StlTooltip>
        <ZoomToRoute
          geometry={route.feature.geometry}
          onClose={onClose}
          className={commonStyles.menu_item}
        />
        <StlTooltip
          title={!canEditOrDelete ? manageRouteDisabledReason : ''}
          placement="left"
        >
          <span style={{ display: 'block' }}>
            <MenuItem
              onClick={handlePrepareDelete}
              disabled={!canEditOrDelete}
              className={`${commonStyles.menu_item} ${commonStyles.menu_item_delete}`}
            >
              <ListItemIcon aria-label="delete" title="Delete">
                <FontAwesomeIcon name="trash" size="0.75rem" />
              </ListItemIcon>
              <Typography variant="caption">Delete Route</Typography>
            </MenuItem>
          </span>
        </StlTooltip>
      </Menu>
      <EditPopup
        title="Rename Route"
        subtitle="Changing the route name will update it across the entire platform"
        txtField={{
          label: 'Route Name',
          value: editRouteName,
          onChange: setEditRouteName,
          maxLength: 100,
          errorMsg: updateNameError(
            route.savedRoute.routeName,
            editRouteName,
            existingNames,
            true,
          ),
        }}
        isOpen={Boolean(isEditing)}
        setIsOpen={() => setIsEditing(null)}
        onApply={handleEditName}
      />
      <ConfirmDeletePopup
        objectType="route"
        isOpen={Boolean(isDeleting)}
        setIsOpen={() => setIsDeleting(null)}
        onApply={handleConfirmDelete}
        name={isDeleting?.savedRoute.routeName ?? ''}
        customMessage={DELETE_ROUTE_CUSTOM_MESSAGE}
      />
    </>
  );
}
export function TempRouteMenu({ route }: { route: TempRoute }) {
  const dispatch = useAppDispatch();
  const [editRouteName, setEditRouteName] = useState<string>(route.routeName);
  const { feature: selectionFeature, basicRouteInfo } = useRouteInfo();
  const { anchorEl, open, onClose, onOpen } = useMenu();
  const savedRoutes = useAppSelector(selectCurrentSavedRoutes);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const project_slug = useAppSelector(selectProject);
  const [createSavedRoute, { data: createSavedRouteData }] =
    useSavedRouteCreateMutation();

  useEffect(() => {
    if (route.routeName) setEditRouteName(route.routeName);
  }, [route?.routeName]);

  const existingNames = useMemo(
    () =>
      savedRoutes?.map((r) => r.savedRoute.routeName.trim().toLowerCase()) ||
      [],
    [savedRoutes],
  );

  const onRouteCancelClick = () => {
    dispatch(setRouteSelection(null));
  };

  const handlePrepareSave = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsSaving(true);
  };

  const handleSave = () => {
    const { waypointSegments, realizedSegments } = basicRouteInfo;

    if (
      !editRouteName ||
      !waypointSegments?.length ||
      !realizedSegments?.length
    )
      return;

    createSavedRoute({
      project_slug: project_slug as string,
      savedRoute: {
        routeName: editRouteName,
        routeSegmentIds: waypointSegments,
        realizedSegments,
      },
    })
      .unwrap()
      .then(() => showToast('success', getSavedSuccessMsg('route')))
      .catch((e) => {
        console.error(e);
        showToast('error', SOMETHING_WENT_WRONG_SAVING_MSG);
      });
  };

  useEffect(() => {
    if (createSavedRouteData?.id)
      dispatch(setRouteSelection(createSavedRouteData.id));
  }, [createSavedRouteData]);

  return (
    <>
      <IconButton
        size="small"
        className={styles.save}
        edge="end"
        title="Add"
        onClick={handlePrepareSave}
      >
        <Done />
      </IconButton>
      <IconButton
        size="small"
        className={styles.close}
        edge="end"
        title="Cancel"
        onClick={onRouteCancelClick}
      >
        <Close />
      </IconButton>
      <IconButton
        size="small"
        aria-controls={open ? 'route-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        title="More"
        onClick={onOpen}
      >
        <FontAwesomeIcon name="ellipsis-vertical" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        className={styles.menu}
      >
        <OpenTravelTimeMenuItem id={route.id} onClose={onClose} />
        <ZoomToRoute
          geometry={selectionFeature?.geometry}
          className={commonStyles.menu_item}
          onClose={onClose}
        />
      </Menu>
      <EditPopup
        title="Save as a New Route"
        subtitle="Save your temporary route as a new route for later use 
across the project"
        txtField={{
          label: 'Route Name',
          value: editRouteName,
          onChange: setEditRouteName,
          maxLength: 100,
          errorMsg: updateNameError(
            route.routeName,
            editRouteName,
            existingNames,
            true,
          ),
        }}
        btnTxt="Save Route"
        isOpen={isSaving}
        setIsOpen={setIsSaving}
        onApply={handleSave}
      />
    </>
  );
}

function OpenTravelTimeMenuItem({
  id,
  onClose,
}: {
  id: string;
  onClose: (e?: React.MouseEvent<HTMLElement>) => void;
}) {
  const dispatch = useAppDispatch();

  const handleOpenTravelTime = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (id !== 'temp') dispatch(setRouteSelection(id));
      dispatch(setWorkflow(WorkflowType.TravelTime));
      onClose();
    },
    [dispatch, id, onClose],
  );

  return (
    <MenuItem onClick={handleOpenTravelTime} className={commonStyles.menu_item}>
      <ListItemIcon aria-label="travelTime" title="travelTime">
        <FontAwesomeIcon name="arrow-up-right-from-square" size="0.75rem" />
      </ListItemIcon>
      <Typography variant="caption">Open Travel Time</Typography>
    </MenuItem>
  );
}
